import { useEffect, useRef, useState } from "react";
import { loadPrizes, saveAllPrizes, getType, getColor, SIZES, isTrophy, isCoin } from "./prizes.js";
import { t } from "./i18n.js";
import AdSlot from "./AdSlot.jsx";
import PlushImg from "./PlushImg.jsx";
import CoinImg from "./CoinImg.jsx";

/* Prize room with drag & drop: hold a plush (~1/3 s) to pick it up, drag to a
   new spot, release. A quick swipe still scrolls the page. Small/medium
   swap places on the shelves, big ones on the floor. Order persists. */

const HOLD_MS = 330;      // long-press before a drag starts
const HOLD_SLOP = 12;     // finger drift that cancels the hold (= scroll intent)

function Plush({ p, group, hidden, onPointerDown }) {
  const ty = getType(p.type), c = getColor(p.color);
  if (!ty || !c) return null;
  return (
    <span
      className="pr-plush"
      data-plush={p.id}
      data-group={group}
      title={`${t("color_" + p.color)} ${t("type_" + p.type).toLowerCase()} (${t("size_" + p.size).toLowerCase()}) – ${p.game}${p.owner && p.owner !== "?" ? " · " + p.owner : ""}`}
      style={{ opacity: hidden ? 0.25 : 1 }}
      onPointerDown={(e) => onPointerDown(e, p, group)}
      onContextMenu={(e) => e.preventDefault()}
    >
      <PlushImg type={p.type} color={p.color} px={SIZES[p.size].px} />
    </span>
  );
}

// Stable reference: add/removeEventListener must get the SAME function, or the
// listener leaks and its preventDefault kills page scroll everywhere afterwards.
const blockScroll = (e) => e.preventDefault();

/* PIXEL-pakkede hylder (2026-07-15, foer: fast 12-units-kvote der spildte
   plads paa pc): hylden fyldes op efter den MAALTE bredde, saa desktop faar
   mange flere bamser pr. hylde og ingen halvtomme hylder. Bredden pr. bamse
   anslaas som dens hoejde (renders er ca. kvadratiske) + flex-gap. */
const ITEM_GAP = 10;
const plushW = (p) => SIZES[p.size].px + ITEM_GAP;
const packShelves = (items, availPx) => {
  const shelves = [];
  let row = [], used = 0;
  for (const p of items) {
    const w = plushW(p);
    if (row.length && used + w > availPx) { shelves.push(row); row = []; used = 0; }
    row.push(p); used += w;
  }
  shelves.push(row);
  if (availPx - used < plushW({ size: "mellem" })) shelves.push([]); // room for one more
  while (shelves.length < 2) shelves.push([]);
  return shelves;
};

export default function PrizeRoom({ onExit }) {
  const [prizes, setPrizes] = useState(() => loadPrizes());
  const [drag, setDrag] = useState(null);     // {id, group, x, y}
  const dragRef = useRef(null);
  const holdRef = useRef(null);               // {timer, prize, group, sx, sy}

  const trophies = prizes.filter(isTrophy);
  const coins = prizes.filter(isCoin);
  const plush = prizes.filter((p) => !isTrophy(p) && !isCoin(p));
  const shelfItems = plush.filter((p) => SIZES[p.size].shelf);
  const floorItems = plush.filter((p) => !SIZES[p.size].shelf);
  /* hyldebredden maales live (som pokalhylden), saa pakningen foelger skaermen */
  const [roomW, setRoomW] = useState(0);
  const roomRO = useRef(null);
  const roomRef = (el) => {
    if (roomRO.current) { roomRO.current.disconnect(); roomRO.current = null; }
    if (el) {
      const w = () => setRoomW(el.clientWidth);
      const ro = new ResizeObserver(w);
      ro.observe(el);
      roomRO.current = ro;
      w();
    }
  };
  // rummets padding (2x18) + hylderaekkens egen padding (2x10)
  const shelfPx = roomW ? Math.max(160, roomW - 36 - 20) : 380;
  const shelves = packShelves(shelfItems, shelfPx);
  // top shelf: boers-guldmoenter FOERST, saa pokalerne - begge i fast
  // mellembamse-hoejde; the plank width is measured live (same logic on
  // desktop and mobile), and whatever doesn't fit goes into the "+N" badge
  const honor = [
    ...coins.map((c) => ({ ...c, __coin: true })),
    ...trophies,
  ];
  const trophySize = SIZES.mellem.px;
  const T_SLOT = trophySize + 4, T_BADGE = 62;
  const [trophyW, setTrophyW] = useState(0);
  const trophyRO = useRef(null);
  const trophyItemsRef = (el) => {
    if (trophyRO.current) { trophyRO.current.disconnect(); trophyRO.current = null; }
    if (el) {
      const w = () => setTrophyW(el.clientWidth - 12);   // minus egen padding
      const ro = new ResizeObserver(w);
      ro.observe(el);
      trophyRO.current = ro;
      w();
    }
  };
  const fitAll = Math.max(1, Math.floor(trophyW / T_SLOT));
  const honorFit = honor.length <= fitAll
    ? honor.length
    : Math.max(1, Math.floor((trophyW - T_BADGE) / T_SLOT));
  const shownHonor = trophyW ? honor.slice(0, honorFit) : [];
  const extraHonor = honor.length - shownHonor.length;

  const beginDrag = (prize, group, x, y) => {
    const d = { id: prize.id, group, x, y };
    dragRef.current = d;
    setDrag(d);
    document.addEventListener("touchmove", blockScroll, { passive: false });
  };

  const endDrag = () => {
    document.removeEventListener("touchmove", blockScroll);
    dragRef.current = null;
    setDrag(null);
  };

  const onPointerDown = (e, prize, group) => {
    if (e.button !== undefined && e.button !== 0) return;
    const sx = e.clientX, sy = e.clientY;
    holdRef.current = {
      prize, group, sx, sy,
      timer: setTimeout(() => {
        holdRef.current = null;
        beginDrag(prize, group, sx, sy);
      }, HOLD_MS),
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      const h = holdRef.current;
      if (h && Math.hypot(e.clientX - h.sx, e.clientY - h.sy) > HOLD_SLOP) {
        clearTimeout(h.timer);          // moved too early: it's a scroll, not a drag
        holdRef.current = null;
      }
      const d = dragRef.current;
      if (d) {
        const nd = { ...d, x: e.clientX, y: e.clientY };
        dragRef.current = nd;
        setDrag(nd);
      }
    };
    const onUp = (e) => {
      const h = holdRef.current;
      if (h) { clearTimeout(h.timer); holdRef.current = null; }
      const d = dragRef.current;
      if (!d) return;
      // drop: BYT plads med den naermeste bamse i samme gruppe (2026-07-15,
      // foer: indsaet-ved-siden-af, som forskubbede hele opstillingen)
      const els = [...document.querySelectorAll(`[data-plush][data-group="${d.group}"]`)]
        .filter((el) => el.dataset.plush !== d.id);
      if (els.length) {
        let best = null, bestDist = Infinity;
        for (const el of els) {
          const r = el.getBoundingClientRect();
          const cxm = r.left + r.width / 2, cym = r.top + r.height / 2;
          const dist = Math.hypot(e.clientX - cxm, e.clientY - cym);
          if (dist < bestDist) { bestDist = dist; best = el.dataset.plush; }
        }
        setPrizes((prev) => {
          const a = prev.findIndex((p) => p.id === d.id);
          const b = prev.findIndex((p) => p.id === best);
          if (a < 0 || b < 0) return prev;
          const arr = [...prev];
          [arr[a], arr[b]] = [arr[b], arr[a]];
          saveAllPrizes(arr);
          return arr;
        });
      }
      endDrag();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.removeEventListener("touchmove", blockScroll);
    };
  }, []);

  const dragPrize = drag ? prizes.find((p) => p.id === drag.id) : null;

  return (
    <div className="pr-root">
      <style>{css}</style>
      <header className="pr-head">
        <button className="pr-back" onClick={onExit}>{t("back")}</button>
        <h1>{t("pr_title")}</h1>
        <span className="pr-count">{prizes.length} {prizes.length === 1 ? t("pr_count_one") : t("pr_count_many")}</span>
      </header>
      {plush.length > 1 && (
        <p className="pr-tip">{t("pr_tip")}</p>
      )}

      <div className="pr-room" ref={roomRef}>
        {prizes.length === 0 && (
          <div className="pr-empty">{t("pr_empty")}<br />{t("pr_empty2")}</div>
        )}

        {/* honour shelf: boers-moenter + pokaler; overflow collapses into "+N" */}
        {honor.length > 0 && (
          <div className="pr-trophyshelf">
            <div className="pr-trophyitems" ref={trophyItemsRef}>
              {shownHonor.map((p) => p.__coin ? (
                <CoinImg key={p.id} className="pr-coin" px={trophySize}
                  title={`${t("ex_coin_one")} – ${p.game}${p.owner && p.owner !== "?" ? " · " + p.owner : ""}`} />
              ) : (
                <img key={p.id} className="pr-trophy" src="/images/trophy.png?v=2" alt={t("trophy")}
                  style={{ height: trophySize }}
                  title={`${t("trophy")} – ${p.game}${p.owner && p.owner !== "?" ? " · " + p.owner : ""}`} />
              ))}
              {extraHonor > 0 && <span className="pr-trophymore">+{extraHonor}</span>}
            </div>
            <div className="pr-goldplank" />
          </div>
        )}

        {shelves.map((row, i) => (
          <div key={i} className="pr-shelf">
            <div className="pr-shelfitems">
              {row.map((p) => (
                <Plush key={p.id} p={p} group="shelf" hidden={drag && drag.id === p.id} onPointerDown={onPointerDown} />
              ))}
            </div>
            <div className="pr-plank" />
          </div>
        ))}

        <div className="pr-floor">
          <div className="pr-flooritems">
            {floorItems.map((p) => (
              <Plush key={p.id} p={p} group="floor" hidden={drag && drag.id === p.id} onPointerDown={onPointerDown} />
            ))}
          </div>
          <div className="pr-rug" />
        </div>
      </div>

      <AdSlot name="prizeroom" height={100} />

      {/* the plush riding along under the finger */}
      {drag && dragPrize && (
        <span className="pr-ghost" style={{ left: drag.x, top: drag.y }}>
          <PlushImg type={dragPrize.type} color={dragPrize.color} px={SIZES[dragPrize.size].px} />
        </span>
      )}
    </div>
  );
}

const css = `
.pr-root{
  min-height:100vh; background:#1d3318;
  background-image:
    radial-gradient(circle at 50% -10%, rgba(120,190,90,.22) 0%, rgba(0,0,0,0) 55%),
    repeating-linear-gradient(45deg, rgba(214,178,94,.05) 0 2px, transparent 2px 14px),
    repeating-linear-gradient(-45deg, rgba(214,178,94,.05) 0 2px, transparent 2px 14px),
    linear-gradient(180deg,#24401f,#14230d);
  font-family:'Nunito',system-ui,sans-serif; color:#F6EED9;
  display:flex; flex-direction:column; align-items:center; padding-bottom:28px;
}
.pr-head{
  width:min(96%,760px); display:flex; align-items:center; justify-content:space-between;
  gap:8px; padding:14px 4px 8px;
}
.pr-head h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(17px,5vw,28px);
  margin:0; letter-spacing:2px;
  background:linear-gradient(180deg,#f7e08a 18%,#d4a943 55%,#9a6c1c 100%);
  -webkit-background-clip:text; background-clip:text; color:transparent;
  filter:drop-shadow(0 2px 1px rgba(0,0,0,.55));
}
.pr-back{
  background:linear-gradient(#6b452a,#4a2c16); color:#f2e7c9;
  border:1px solid #c9a24b; border-radius:9px;
  font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #2c1a0e, inset 0 1px 0 rgba(255,255,255,.15);
  white-space:nowrap; flex-shrink:0;
}
.pr-count{
  font-weight:800; font-size:12px; color:#f2dc9a;
  background:linear-gradient(#2e4a26,#1d3318);
  border:2px solid #c9a24b; border-radius:10px; padding:4px 10px;
  box-shadow:0 2px 5px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.12);
  white-space:nowrap; flex-shrink:0;
}
.pr-tip{margin:0 0 8px; font-family:Georgia,'Times New Roman',serif; font-size:13px; color:#e9dfc0; opacity:.9;}
.pr-room{
  width:min(96%,760px);
  background:#4a2f1c;
  background-image:
    radial-gradient(ellipse at 50% 0%, rgba(255,190,90,.20) 0%, rgba(255,190,90,0) 55%),
    repeating-linear-gradient(90deg, rgba(0,0,0,.10) 0 2px, transparent 2px 46px),
    linear-gradient(180deg,#553622,#3f2412);
  border:6px solid #2c1a0e; border-radius:18px; margin-top:2px;
  padding:26px 18px 0;
  box-shadow:inset 0 0 0 3px #7a5230, inset 0 0 46px rgba(0,0,0,.5), 0 14px 34px rgba(0,0,0,.5);
  position:relative;
}
.pr-empty{
  text-align:center; font-family:Georgia,'Times New Roman',serif; font-weight:700; font-size:16px;
  padding:8px 0 4px; color:#f2e7c9; text-shadow:0 2px 4px rgba(0,0,0,.5);
}
.pr-trophyshelf{margin-bottom:14px; position:relative;}
.pr-trophyshelf::after{
  content:""; position:absolute; left:10px; right:10px; bottom:10px; height:3px;
  background:linear-gradient(#f4dc8a,#a8821f); border-radius:2px;
  box-shadow:0 1px 2px rgba(0,0,0,.5); pointer-events:none;
}
.pr-trophyitems{
  display:flex; align-items:flex-end; justify-content:center; gap:4px;
  min-height:58px; padding:0 6px;
}
.pr-trophy{
  display:block; width:auto;
  filter:drop-shadow(0 0 8px rgba(255,205,80,.55)) drop-shadow(0 3px 3px rgba(0,0,0,.45));
}
.pr-coin{
  filter:drop-shadow(0 0 10px rgba(255,215,94,.65)) drop-shadow(0 3px 3px rgba(0,0,0,.45));
}
.pr-trophymore{
  align-self:center; font-family:'Alfa Slab One',Georgia,serif; font-size:15px;
  color:#f0d67a;
  background:linear-gradient(#33552a,#1d3318);
  border:2px solid #c9a24b; border-radius:16px 16px 6px 6px; padding:8px 12px;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.15), 0 2px 5px rgba(0,0,0,.45);
}
.pr-goldplank{
  height:13px; border-radius:3px;
  background:linear-gradient(180deg,#8a5a33 0%,#6b4526 45%,#4a2c16 100%);
  box-shadow:0 4px 8px rgba(0,0,0,.5), inset 0 2px 0 #f0d67a, inset 0 4px 3px rgba(0,0,0,.25);
}
.pr-shelf{margin-bottom:10px; position:relative;}
.pr-shelf::after{
  content:""; position:absolute; left:10px; right:10px; bottom:10px; height:3px;
  background:linear-gradient(#f4dc8a,#a8821f); border-radius:2px;
  box-shadow:0 1px 2px rgba(0,0,0,.5); pointer-events:none;
}
.pr-shelfitems{
  display:flex; align-items:flex-end; justify-content:flex-start; gap:10px;
  min-height:58px; padding:0 10px;
}
.pr-plank{
  height:13px; border-radius:3px;
  background:linear-gradient(180deg,#8a5a33 0%,#6b4526 45%,#4a2c16 100%);
  box-shadow:0 4px 7px rgba(0,0,0,.45), inset 0 2px 0 #d9b05e, inset 0 4px 3px rgba(0,0,0,.25);
}
.pr-floor{margin:18px -18px 0;}
.pr-flooritems{
  display:flex; align-items:flex-end; justify-content:center; gap:14px;
  min-height:100px; padding:0 20px; flex-wrap:wrap;
}
.pr-rug{
  height:34px; border-radius:0 0 12px 12px;
  background:linear-gradient(180deg,#3f2412,#2c1a0e);
  box-shadow:inset 0 2px 0 rgba(217,176,94,.7), inset 0 10px 14px rgba(0,0,0,.4);
}
.pr-plush{
  line-height:1; filter:drop-shadow(0 3px 3px rgba(0,0,0,.4)); cursor:grab;
  -webkit-user-select:none; user-select:none; -webkit-touch-callout:none;
  touch-action:pan-y;   /* let the page scroll vertically from a plush; hold-to-drag takes over */
}
.pr-ghost{
  position:fixed; transform:translate(-50%,-60%) scale(1.18); line-height:1;
  pointer-events:none; z-index:50;
}
`;
